import { useMemo } from 'react';
import { RaporCekmecesi } from '../rapor-ekrani/RaporCekmecesi';
import { RaporTablosu } from '../rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../rapor-ekrani/RaporHatasi';
import { useRaporSorgu } from '../rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import { paraMetni, tarihMetni, yuzdeMetni } from '../rapor-ekrani/bicim';
import type { RaporKolonu } from '../rapor-ekrani/kolon';
import type { PanelEkranKaynagi } from '../../types/panelConfig';
import { Kutu } from '../stok-rapor/StokSuzgecleri';
import {
  URUN_SINIRI, aracUrunSatirlari, karYuzdesi, kari, maliyeti, toplamKarYuzdesi,
  type AracSatiri, type AracUrunSatiri, type HamAracUrunSatiri, type MaliyetTuru,
} from './tipler';

/** Bilgi satırındaki ve çekmece alt başlığındaki kapsam notu. */
export const MOBIL_NOTU = 'yalnız mobil satışlar';

interface Props {
  /** Açık araç; `undefined` ise çekmece kapalıdır. */
  arac: AracSatiri | undefined;
  kaynak: PanelEkranKaynagi | undefined;
  /** Listenin dönemi, kapsamı ve maliyet türü: çekmece aynı süzgeçle açılır. */
  bas: string;
  bit: string;
  mobil: boolean;
  maliyetTuru: MaliyetTuru;
  onKapat: () => void;
}

/** S350: aracın dönem özeti (listedeki satırdan) ve en çok satan ürünleri, sağdan açılan çekmecede. */
export function AracSatisCekmecesi({ arac, bas, bit, mobil, onKapat, ...ic }: Props) {
  const alt = [`${tarihMetni(bas)} - ${tarihMetni(bit)}`, mobil && MOBIL_NOTU].filter(Boolean).join(' · ');
  return (
    <RaporCekmecesi acik={!!arac} baslik={arac?.ad ?? ''} altBaslik={arac ? alt : undefined} onKapat={onKapat} testid="arac-cekmece">
      {/* `key`: başka araca geçince önceki aracın satırları taşınmaz. */}
      {arac && <Icerik key={arac.id} arac={arac} bas={bas} bit={bit} mobil={mobil} {...ic} />}
    </RaporCekmecesi>
  );
}

const ALT_BASLIK = 'm-0 text-[12px] font-semibold uppercase tracking-[0.04em] text-ink-500';

function Icerik({ arac, kaynak, bas, bit, mobil, maliyetTuru }: Omit<Props, 'onKapat' | 'arac'> & { arac: AracSatiri }) {
  const telefon = useDarEkran();
  // Maliyet türü ds parametresi değil: üç maliyet satırda hazır (tür değişince sorgu yinelenmez).
  const q = useRaporSorgu<HamAracUrunSatiri>(kaynak, {
    '@LOKASYON': arac.id, '@BASTARIH': bas, '@BITTARIH': bit, '@MOBIL': mobil ? '1' : '',
  });
  const urunler = useMemo(() => aracUrunSatirlari(q.data ?? []), [q.data]);
  const kolonlar = useMemo<RaporKolonu<AracUrunSatiri>[]>(() => [
    { id: 'stok_kodu', baslik: 'Stok Kodu', tur: 'kod', deger: (u) => u.kod, kart: 'alt' },
    { id: 'urun', baslik: 'Ürün', tur: 'metin', deger: (u) => u.ad, esnek: true, kart: 'baslik' },
    { id: 'net_satis', baslik: 'Net Satış', tur: 'para', deger: (u) => u.net, toplam: true, kart: 'olcu' },
    { id: 'maliyet', baslik: 'Maliyet', tur: 'para', deger: (u) => maliyeti(u, maliyetTuru), toplam: true, kart: 'ikincil' },
    { id: 'kar', baslik: 'Kâr', tur: 'para', deger: (u) => kari(u, maliyetTuru), toplam: true, kart: 'ikincil' },
    {
      id: 'kar_yuzde', baslik: 'Kâr %', tur: 'yuzde', kart: 'ikincil',
      deger: (u) => karYuzdesi(kari(u, maliyetTuru), maliyeti(u, maliyetTuru)),
      toplam: (satirlar) => toplamKarYuzdesi(satirlar, maliyetTuru),
    },
  ], [maliyetTuru]);

  const maliyet = maliyeti(arac, maliyetTuru);
  const kar = kari(arac, maliyetTuru);

  return (
    <div className={telefon ? 'flex-1 flex flex-col gap-2 px-4 pt-3 rk-kenar-16' : 'flex-1 min-h-0 flex flex-col gap-3 p-4'}>
      <div className="flex flex-wrap gap-2.5" data-testid="arac-ozet">
        <Kutu etiket="Net satış">{paraMetni(arac.net)}</Kutu>
        <Kutu etiket="Maliyet">{paraMetni(maliyet)}</Kutu>
        <Kutu etiket="Kâr">{paraMetni(kar)}</Kutu>
        <Kutu etiket="Kâr %">{yuzdeMetni(karYuzdesi(kar, maliyet))}</Kutu>
      </div>

      <h3 className={ALT_BASLIK}>En çok satan {URUN_SINIRI} ürün</h3>
      {!kaynak ? (
        <RaporHatasi testid="arac-urun-kaynaksiz" mesaj="Bu firmada araç ürün kırılımı kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="arac-urun-hata" mesaj="Ürün kırılımı yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <RaporTablosu
          testid="arac-urun-tablosu"
          etiket={`${arac.ad} en çok satan ürünler`}
          className={telefon ? '' : 'min-h-[140px]'}
          kartStili="cizgi"
          kolonlar={kolonlar}
          satirlar={urunler}
          satirKimligi={(u) => u.id}
          // Sınır dolduysa toplam aracın tamamı DEĞİL, yalnız gösterilen ürünlerindir.
          toplamEtiketi={urunler.length >= URUN_SINIRI ? `İlk ${URUN_SINIRI} toplamı` : 'Toplam'}
          bosMesaj="Bu dönemde satılan ürün yok."
          yukleniyor={q.isLoading}
          yenileniyor={q.isFetching && !q.isLoading}
        />
      )}
    </div>
  );
}
